import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createJob } from '../../api/cron';
import { useSnackbar } from '../../context/SnackbarContext';
import { CronIntervalBuilder } from './CronIntervalBuilder';

interface Props {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_USERS = ['root', 'www-data', 'ubuntu', 'webadmin'];

export function CreateJobModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [user, setUser] = useState('webadmin');
  const [command, setCommand] = useState('');
  const [expression, setExpression] = useState('0 3 * * *');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => createJob({ name: name.trim(), description: description.trim() || undefined, expression, command: command.trim(), user }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cron', 'jobs'] });
      showSnackbar(`Cron job "${name}" created`, 'success');
      handleClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  function handleClose() {
    setName(''); setDescription(''); setUser('webadmin'); setCommand(''); setExpression('0 3 * * *'); setError('');
    onClose();
  }

  const valid = name.trim().length > 0 && command.trim().length > 0 && expression.trim().split(/\s+/).length === 5;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create cron job</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              required
              inputProps={{ maxLength: 80 }}
              sx={{ flex: 2 }}
            />
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Run as user</InputLabel>
              <Select value={user} label="Run as user" onChange={(e) => setUser(e.target.value)}>
                {DEFAULT_USERS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            inputProps={{ maxLength: 500 }}
          />

          <TextField
            label="Command"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            size="small"
            required
            placeholder="/usr/local/bin/my-script.sh"
            inputProps={{ style: { fontFamily: 'monospace' } }}
            helperText="Must be an absolute path or start with a known interpreter (node, python3, bash…)"
          />

          <Typography variant="subtitle2" color="text.secondary">Schedule</Typography>
          <CronIntervalBuilder value={expression} onChange={setExpression} />

          {error && <Typography color="error" variant="caption">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Create job
        </Button>
      </DialogActions>
    </Dialog>
  );
}
